/**
 * Procedural sound effects — pure Web Audio API, no external dependencies.
 */

let audioCtx = null;

/**
 * Whether a gesture has happened. NOTHING creates an AudioContext before it.
 *
 * WHY THIS FLAG EXISTS. `getAudioCtx` used to construct the context on first
 * call, and every play function calls it — so the context was built long before
 * anyone touched the page, in the suspended state the autoplay policy requires,
 * and Chrome logged "The AudioContext was not allowed to start" on every
 * attempt to play into it.
 *
 * On a normal page that is a handful of lines. On the ATTRACT PAGE it is
 * thousands: the shell embeds an arena running a permanent bot fight, firing
 * hits and jumps and deaths on a loop, and nobody ever presses a key in that
 * iframe — so it never unlocks and never stops trying. The founder's console
 * had 1948 entries suppressed.
 *
 * Silence is the correct behaviour there. An arena playing behind a menu should
 * not make noise even if it could.
 */
let unlocked = false;

/** Null until a gesture. Callers must tolerate it — see `playSound`. */
export function getAudioCtx() {
  if (!unlocked) return null;
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
}

export function createTone(waveType) {
  const ctx = getAudioCtx();
  if (!ctx) return null;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = waveType;
  return { ctx, osc, gain, t: ctx.currentTime };
}

export function playSound(fn) {
  // ONE guard for every sound in the file. Checking here rather than in each
  // `play*` means a new sound cannot forget to check, and a locked page does no
  // work at all rather than building an oscillator to throw it away.
  if (!unlocked) return;
  try { fn(); } catch { /* audio not available */ }
}

/** `volume` lets remote players' jumps play quieter than your own. */
export function playJumpSound(volume = 0.15) {
  playSound(() => {
    const { osc, gain, t } = createTone('sine');
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(600, t + 0.1);
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.start(t);
    osc.stop(t + 0.15);
  });
}

/** Landing thump. `intensity` 0-1 scales with fall speed; the floor answers back. */
export function playLandSound(intensity = 1, volume = 1) {
  playSound(() => {
    const { ctx, osc, gain, t } = createTone('sine');
    const filter = ctx.createBiquadFilter();
    osc.disconnect();
    osc.connect(filter);
    filter.connect(gain);
    filter.type = 'lowpass';
    filter.frequency.value = 240;
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(55, t + 0.1);
    const g = (0.05 + 0.09 * Math.min(1, Math.max(0, intensity))) * volume;
    gain.gain.setValueAtTime(g, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
    osc.start(t);
    osc.stop(t + 0.16);
  });
}

/** One footfall. `alt` alternates pitch so a stride reads as two feet. */
export function playFootstep(alt = false) {
  playSound(() => {
    const { ctx, osc, gain, t } = createTone('triangle');
    const filter = ctx.createBiquadFilter();
    osc.disconnect();
    osc.connect(filter);
    filter.connect(gain);
    filter.type = 'lowpass';
    filter.frequency.value = 320;
    osc.frequency.setValueAtTime(alt ? 98 : 82, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.05);
    gain.gain.setValueAtTime(0.05, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
    osc.start(t);
    osc.stop(t + 0.08);
  });
}

/** Soft two-note chime for a body appearing — match start and gym respawns. */
export function playSpawnSound() {
  playSound(() => {
    const notes = [660, 990];
    notes.forEach((freq, i) => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.06, t + i * 0.07);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.35);
      osc.start(t + i * 0.07);
      osc.stop(t + i * 0.07 + 0.35);
    });
  });
}

export function playDeathSound() {
  playSound(() => {
    const { osc, gain, t } = createTone('sawtooth');
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.5);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.start(t);
    osc.stop(t + 0.5);
  });
}

export function playCollectSound() {
  playSound(() => {
    const notes = [523, 659, 784]; // C5, E5, G5 arpeggio
    notes.forEach((freq, i) => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, t + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.08 + 0.2);
      osc.start(t + i * 0.08);
      osc.stop(t + i * 0.08 + 0.2);
    });
  });
}

export function playCountdownBeep(pitch = 440) {
  playSound(() => {
    const { osc, gain, t } = createTone('square');
    osc.frequency.value = pitch;
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.start(t);
    osc.stop(t + 0.2);
  });
}

/**
 * The drop. Everyone has landed, nobody can be hurt yet, and the match has
 * begun — a low horn that rises, so it reads as "here we go" rather than as an
 * alert. Deliberately unlike anything in combat: this is the only time it plays.
 */
export function playMatchStartHorn() {
  playSound(() => {
    const { osc, gain, t } = createTone('sawtooth');
    osc.frequency.setValueAtTime(110, t);
    osc.frequency.exponentialRampToValueAtTime(220, t + 0.55);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.09, t + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
    osc.start(t);
    osc.stop(t + 0.7);
  });
}

/**
 * Damage is live. The one sound in the game that means "you can die now", so it
 * is the brightest and the shortest — a stab, not a fanfare. The 3-2-1 beeps
 * climb toward this pitch, which is what makes them read as leading somewhere.
 */
export function playFightSound() {
  playSound(() => {
    for (const [i, freq] of [880, 1319].entries()) {
      const { osc, gain, t } = createTone('square');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, t + i * 0.07);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.32);
      osc.start(t + i * 0.07);
      osc.stop(t + i * 0.07 + 0.32);
    }
  });
}

export function playWinFanfare() {
  playSound(() => {
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, t + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.4);
      osc.start(t + i * 0.12);
      osc.stop(t + i * 0.12 + 0.4);
    });
  });
}

export function playSpellSound() {
  playSound(() => {
    const { ctx, osc, gain, t } = createTone('sawtooth');
    const filter = ctx.createBiquadFilter();
    osc.disconnect();
    osc.connect(filter);
    filter.connect(gain);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, t);
    filter.frequency.exponentialRampToValueAtTime(200, t + 0.4);
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(200, t + 0.4);
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.start(t);
    osc.stop(t + 0.4);
  });
}

export function playCrackSound() {
  playSound(() => {
    const { osc, gain, t } = createTone('sawtooth');
    osc.frequency.setValueAtTime(400, t);
    osc.frequency.exponentialRampToValueAtTime(200, t + 0.15);
    gain.gain.setValueAtTime(0.08, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.start(t);
    osc.stop(t + 0.15);
  });
}

export function playBreakSound() {
  playSound(() => {
    const { osc, gain, t } = createTone('square');
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.2);
    gain.gain.setValueAtTime(0.1, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    osc.start(t);
    osc.stop(t + 0.25);
  });
}

export function playBounceSound() {
  playSound(() => {
    const notes = [300, 600, 800];
    notes.forEach((freq, i) => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, t + i * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.15);
      osc.start(t + i * 0.06);
      osc.stop(t + i * 0.06 + 0.15);
    });
  });
}

// ── Combat (new in v2) ─────────────────────────────────────────────────────
//
// v1 had no combat, so it had no combat audio. Each ability gets a distinct
// timbre because in an 8-player fight the sound is often the only warning you
// get that something is being cast behind you.

/** Landing a hit. `heavy` widens and lowers it — the ultimate must sound bigger. */
export function playHitSound(heavy = false) {
  playSound(() => {
    const { ctx, osc, gain, t } = createTone(heavy ? 'square' : 'triangle');
    const filter = ctx.createBiquadFilter();
    osc.disconnect();
    osc.connect(filter);
    filter.connect(gain);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(heavy ? 1400 : 2600, t);
    filter.frequency.exponentialRampToValueAtTime(heavy ? 180 : 420, t + 0.12);
    osc.frequency.setValueAtTime(heavy ? 220 : 420, t);
    osc.frequency.exponentialRampToValueAtTime(heavy ? 70 : 150, t + 0.11);
    gain.gain.setValueAtTime(heavy ? 0.18 : 0.11, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + (heavy ? 0.22 : 0.13));
    osc.start(t);
    osc.stop(t + 0.24);
  });
}

/** Taking a hit. Duller and lower than dealing one, so you can tell them apart. */
export function playHurtSound() {
  playSound(() => {
    const { osc, gain, t } = createTone('sawtooth');
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.18);
    gain.gain.setValueAtTime(0.13, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.start(t);
    osc.stop(t + 0.2);
  });
}

/** The wind-up. Rising pitch over `windupMs` is the audible half of a telegraph. */
export function playCastSound(windupMs, pitch = 260) {
  playSound(() => {
    const seconds = Math.max(0.06, windupMs / 1000);
    const { osc, gain, t } = createTone('sine');
    osc.frequency.setValueAtTime(pitch, t);
    osc.frequency.exponentialRampToValueAtTime(pitch * 2.2, t + seconds);
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.exponentialRampToValueAtTime(0.09, t + seconds * 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, t + seconds + 0.08);
    osc.start(t);
    osc.stop(t + seconds + 0.1);
  });
}

export function playDashSound() {
  playSound(() => {
    const { ctx, osc, gain, t } = createTone('sawtooth');
    const filter = ctx.createBiquadFilter();
    osc.disconnect();
    osc.connect(filter);
    filter.connect(gain);
    filter.type = 'bandpass';
    filter.Q.value = 4;
    filter.frequency.setValueAtTime(400, t);
    filter.frequency.exponentialRampToValueAtTime(3200, t + 0.16);
    osc.frequency.setValueAtTime(180, t);
    gain.gain.setValueAtTime(0.09, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.start(t);
    osc.stop(t + 0.2);
  });
}

/** Kill confirmation. Deliberately the most satisfying sound in the game. */
export function playKillSound() {
  playSound(() => {
    const notes = [880, 1175, 1568];
    notes.forEach((freq, i) => {
      const { osc, gain, t } = createTone('triangle');
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.13, t + i * 0.055);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.055 + 0.3);
      osc.start(t + i * 0.055);
      osc.stop(t + i * 0.055 + 0.3);
    });
  });
}

/** Ultimate ready. One note, unmistakable, once. */
export function playUltReadySound() {
  playSound(() => {
    const { osc, gain, t } = createTone('sine');
    osc.frequency.setValueAtTime(523, t);
    osc.frequency.setValueAtTime(1047, t + 0.12);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.start(t);
    osc.stop(t + 0.5);
  });
}

/** Zone contraction warning. Low, urgent, and repeated by the caller. */
export function playZoneWarnSound() {
  playSound(() => {
    const { osc, gain, t } = createTone('square');
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.6);
    gain.gain.setValueAtTime(0.07, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.65);
    osc.start(t);
    osc.stop(t + 0.65);
  });
}

/** The Chaos Magician announcing itself. Detuned pair — deliberately wrong. */
export function playChaosSound() {
  playSound(() => {
    for (const detune of [0, 17]) {
      const { osc, gain, t } = createTone('sawtooth');
      osc.detune.value = detune;
      osc.frequency.setValueAtTime(90, t);
      osc.frequency.exponentialRampToValueAtTime(700, t + 0.5);
      gain.gain.setValueAtTime(0.06, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
      osc.start(t);
      osc.stop(t + 0.7);
    }
  });
}

/**
 * Low-HP heartbeat. Returns a stop function.
 *
 * The interval is owned by the caller so it cannot outlive the state that
 * started it — a heartbeat that keeps playing after you die is a support ticket.
 */
export function startHeartbeat() {
  const thump = () => {
    playSound(() => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.setValueAtTime(62, t);
      osc.frequency.exponentialRampToValueAtTime(38, t + 0.14);
      gain.gain.setValueAtTime(0.16, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  };
  thump();
  const id = setInterval(thump, 820);
  return () => clearInterval(id);
}

/**
 * Endgame tension bed — the heartbeat pattern generalized into a low pulsing
 * drone. Returns a handle; `setIntensity(0..1)` shortens the pulse interval and
 * thickens the gain, `stop()` ends it. Like the heartbeat, the timer is owned
 * by the caller's handle so it cannot outlive the state that started it.
 */
export function startTensionDrone() {
  let intensity = 0;
  let stopped = false;
  let timer = null;

  const pulse = () => {
    if (stopped) return;
    playSound(() => {
      const { osc, gain, t } = createTone('sine');
      osc.frequency.setValueAtTime(55, t);
      osc.frequency.exponentialRampToValueAtTime(36, t + 0.22);
      gain.gain.setValueAtTime(0.04 + intensity * 0.07, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.32);
    });
    timer = setTimeout(pulse, 1500 - 950 * intensity);
  };
  pulse();

  return {
    setIntensity(t) {
      intensity = Math.min(1, Math.max(0, t));
    },
    stop() {
      stopped = true;
      clearTimeout(timer);
    },
  };
}

/** Browsers require a gesture before audio plays. Call from the first tap. */
export function unlockAudio() {
  try {
    // Set BEFORE the call: `getAudioCtx` refuses to build a context while this
    // is false, so unlocking after asking for one would return null forever.
    unlocked = true;
    const ctx = getAudioCtx();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  } catch {
    /* no audio on this device */
  }
}
